import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBusinessPlan } from "../App";
import { useAuth } from "../contexts/AuthContext";
import { useUserEntitlements } from "../hooks/useUserEntitlements";
import { useBranding } from "../contexts/BrandingContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import axios from "axios";
import { toast } from "sonner";
import SubscriptionStatus from "./SubscriptionStatus";
import CurrencySelector from "./CurrencySelector";
import LanguageSwitcher from "./LanguageSwitcher";
import { CURRENCIES } from "../constants/currencies";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  Building2,
  Building,
  LayoutDashboard,
  Settings,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileSpreadsheet,
  BarChart3,
  Target,
  ChevronRight,
  LogOut,
  User,
  ChevronDown,
  Globe,
  Shield,
  Crown,
  Palette,
  Settings2,
  Calculator,
  Zap,
  Users,
  PieChart,
  ShoppingCart,
  CreditCard,
  UserPlus,
  Award,
  FileText,
  Activity,
  Brain,
  PlayCircle,
  Link as LinkIcon,
  Download,
  Menu,
  X,
  Edit,
  Trash2,
  FileCheck,
  Archive
} from "lucide-react";

const Navigation = ({ userType = 'new', refreshUserType, userTypeLoading = false }) => {
  const location = useLocation();
  const { t } = useTranslation(['navigation', 'common']);
  const { currentPlan, activeScenario, setActiveScenario, user, createNewPlan, setCurrentPlan } = useBusinessPlan();
  const { logout } = useAuth();
  const { canCreate, entitlements } = useUserEntitlements();
  const { branding, isWhitelabeled } = useBranding();
  const navigate = useNavigate();
  const [showBusinessPlanManager, setShowBusinessPlanManager] = useState(false);
  const [showCreateBusinessPlan, setShowCreateBusinessPlan] = useState(false);
  const [showEditBusinessPlan, setShowEditBusinessPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [scenariosExpanded, setScenariosExpanded] = useState(false);
  const [businessPlanExpanded, setBusinessPlanExpanded] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Toggle section collapse
  const toggleSection = (sectionIndex) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  // Fetch scenarios for current plan
  useEffect(() => {
    const fetchScenarios = async () => {
      if (currentPlan?.id) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/scenarios/${currentPlan.id}`);
          setScenarios(response.data || []);
        } catch (error) {
          console.error('Error fetching scenarios:', error);
          // Fallback to default scenarios if fetch fails
          setScenarios([
            { id: '1', name: 'realistic' },
            { id: '2', name: 'optimistic' },
            { id: '3', name: 'pessimistic' }
          ]);
        }
      }
    };

    fetchScenarios();
  }, [currentPlan?.id, BACKEND_URL]);

  // Fetch business plans and subsidiaries on mount
  useEffect(() => {
    fetchBusinessPlans();
    fetchSubsidiaries();
  }, []);

  const fetchBusinessPlans = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/business-plans`);
      setBusinessPlans(response.data || []);
    } catch (error) {
      console.error("Error fetching business plans:", error);
    }
  };

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true for optimized query
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/enterprise/subsidiaries?company_only=true`);
      setSubsidiaries(response.data || []);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
      setSubsidiaries([]);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowEditBusinessPlan(true);
  };

  const handleDeletePlan = async (plan) => {
    if (businessPlans.length === 1) {
      toast.error("Cannot delete the last business plan");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${plan.name}"?\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/business-plans/${plan.id}`);
        toast.success("Business plan deleted successfully");
        
        // If deleted plan was current, switch to another
        if (currentPlan?.id === plan.id) {
          const remainingPlans = businessPlans.filter(p => p.id !== plan.id);
          setCurrentPlan(remainingPlans[0]);
        }
        
        fetchBusinessPlans();
      } catch (error) {
        toast.error("Failed to delete business plan");
        console.error("Error deleting plan:", error);
      }
    }
  };

  const updatePlan = async (planId, updateData) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/business-plans/${planId}`, updateData);
      toast.success("Business plan updated successfully");
      fetchBusinessPlans();
      
      // Update current plan if it was edited
      if (currentPlan?.id === planId) {
        setCurrentPlan({ ...currentPlan, ...updateData });
      }
    } catch (error) {
      toast.error("Failed to update business plan");
      console.error("Error updating plan:", error);
    }
  };

  // Navigation items based on user type
  const getNavigationItems = () => {
    // For affiliate-only users: Show only affiliate section
    if (userType === 'affiliateOnly') {
      return [
        {
          section: t('navigation:sections.affiliate'),
          items: [
            { path: "/affiliate", label: t('navigation:menu.affiliateDashboard'), icon: Award },
            { action: "createBusinessPlan", label: t('navigation:actions.createBusinessPlan'), icon: Building2 }
          ]
        }
      ];
    }

    // For hybrid and business-only users: Show full menu
    const businessMenu = [
      // Company Settings - Phase 3
      {
        section: t('navigation:sections.companySettings') || "Company Settings",
        items: [
          { path: "/company-settings", label: t('navigation:menu.companySettings'), icon: Settings }
        ]
      },
      {
        section: t('common:labels.overview') || "Overview",
        items: [
          { path: "/dashboard", label: t('navigation:menu.dashboard'), icon: LayoutDashboard },
          { path: "/setup", label: t('navigation:menu.setup'), icon: Settings }
        ]
      },
      {
        section: t('navigation:sections.inputSections'),
        items: [
          { path: "/strategic-framework", label: t('navigation:menu.strategicFramework'), icon: Target },
          { path: "/cost-centers", label: t('navigation:menu.costCenters'), icon: Users },
          { path: "/cost-management", label: t('navigation:menu.costManagement'), icon: DollarSign },
          { path: "/financial-management", label: t('navigation:menu.financialManagement'), icon: Building },
          { path: "/financial-planning", label: t('navigation:menu.financialPlanning'), icon: Target },
          { path: "/enhanced-financial-calculations", label: t('navigation:menu.financialCalculations'), icon: Calculator }
        ]
      },
      {
        section: t('navigation:sections.outputSections'),
        items: [
          { path: "/financial-statements", label: t('navigation:menu.financialStatements'), icon: FileSpreadsheet },
          { path: "/advanced-calculations", label: t('navigation:menu.financialAnalytics'), icon: Calculator },
          { path: "/planning-dashboard", label: t('navigation:menu.planningDashboard'), icon: BarChart3 },
          { path: "/scenario-modeling", label: t('navigation:menu.scenarioModeling'), icon: Zap },
          { path: "/enhanced-export", label: t('navigation:menu.enhancedExport'), icon: Download },
          { path: "/reports", label: "Report Center", icon: FileText }
        ]
      },
      {
        section: t('navigation:sections.performance'),
        items: [
          { path: "/executive-dashboard", label: "Actual Performance Dashboard", icon: Activity }
        ]
      },
      {
        section: t('performance:analysis.financialPerformance') || "Financial Performance",
        items: [
          { path: "/revenue-analysis", label: t('performance:analysis.revenueAnalysis'), icon: TrendingUp },
          { path: "/cost-analysis", label: t('performance:analysis.costAnalysis'), icon: TrendingDown },
          { path: "/profitability-analysis", label: t('performance:analysis.profitabilityAnalysis'), icon: DollarSign },
          { path: "/sales-actuals-entry", label: t('performance:salesActuals.title'), icon: DollarSign },
          { path: "/cost-actuals-entry", label: t('performance:costActuals.title'), icon: TrendingDown }
        ]
      },
      {
        section: t('performance:analysis.strategicAlignment'),
        items: [
          { path: "/strategic-alignment", label: t('performance:analysis.strategicAlignment'), icon: Target }
        ]
      },
      {
        section: t('performance:analysis.varianceAnalytics'),
        items: [
          { path: "/variance-analytics", label: t('performance:analysis.varianceAnalytics'), icon: Target }
        ]
      },
      {
        section: t('performance:analysis.trends'),
        items: [
          { path: "/trends-forecasting", label: t('performance:analysis.trends'), icon: TrendingUp }
        ]
      },
      {
        section: t('navigation:performance.detailedReports'),
        items: [
          { path: "/detailed-reports", label: t('navigation:performance.detailedReports'), icon: FileText }
        ]
      },
      // Subscription & Billing
      {
        section: t('subscription:title') || "Subscription & Billing",
        items: [
          { path: "/subscription", label: t('navigation:menu.mySubscription'), icon: Crown },
          { path: "/pricing", label: t('navigation:menu.pricing'), icon: CreditCard },
          { path: "/add-ons", label: t('navigation:menu.addOns'), icon: ShoppingCart },
          { path: "/my-add-ons", label: t('navigation:menu.myAddOns'), icon: Award },
          { path: "/cart", label: t('common:labels.shoppingCart') || "Shopping Cart", icon: ShoppingCart },
          { path: "/security", label: "Security & 2FA", icon: Shield }
        ]
      }
    ];

    // Add affiliate section for hybrid users
    if (userType === 'hybrid') {
      businessMenu.push({
        section: t('navigation:sections.affiliate'),
        items: [
          { path: "/affiliate", label: t('navigation:menu.affiliateDashboard'), icon: Award }
        ]
      });
    }

    // Add "Apply as Affiliate" for business-only users
    if (userType === 'businessOnly') {
      businessMenu.push({
        section: t('navigation:sections.affiliate'),
        items: [
          { path: "/affiliate/register", label: t('affiliate:registration.title') || "Apply as Affiliate", icon: Users }
        ]
      });
    }

    // Add admin section if admin
    if (user?.is_admin) {
      businessMenu.push({
        section: t('navigation:sections.administration'),
        items: [
          { path: "/admin", label: t('navigation:menu.adminDashboard'), icon: Crown },
          { path: "/admin/feature-governance", label: t('admin:platform.features'), icon: Shield },
          { path: "/cart-analytics", label: t('analytics:charts.cartAnalytics') || "Cart Analytics", icon: BarChart3 },
          { path: "/admin/platform", label: t('navigation:menu.platformAdmin'), icon: Settings2 },
          { path: "/admin/whitelabel", label: t('admin:platform.whiteLabel'), icon: Palette },
          { path: "/admin/affiliates", label: t('admin:affiliates.title'), icon: Users },
          { path: "/admin/add-ons", label: t('admin:addOns.title'), icon: ShoppingCart }
        ]
      });
      
      // Bank Edition - Complete Feature Set
      businessMenu.push({
        section: "BANK EDITION",
        items: [
          { path: "/bank/tenants", label: "Tenants", icon: Building2 },
          { path: "/bank/organizations", label: "Organizations", icon: Building },
          { path: "/bank/users", label: "Users", icon: Users },
          { path: "/bank/review-queue", label: "Review Queue", icon: FileText },
          { path: "/bank/approval-queue", label: "Approvals", icon: Shield },
          { path: "/bank/templates", label: "Templates", icon: FileSpreadsheet },
          { path: "/bank/plan/create", label: "Create Plan", icon: Edit },
          { path: "/bank/budget-targets", label: "Budget Targets", icon: Target },
          { path: "/bank/cascade", label: "Target Cascade", icon: TrendingDown },
          { path: "/bank/consolidation", label: "Consolidation", icon: BarChart3 },
          { path: "/bank/variance", label: "Variance", icon: Activity },
          { path: "/bank/credit-pack", label: "Credit Packs", icon: FileCheck },
          { path: "/bank/evidence-pack", label: "Evidence Packs", icon: Archive },
          { path: "/bank/export-jobs", label: "Export Jobs", icon: Download }
        ]
      });
    }

    return businessMenu;
  };

  const navigationItems = getNavigationItems();

  const handleLogout = () => {
    logout();
  };

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className="nav-sidebar">
      {/* Header with User Menu */}
      <div className="p-4 border-b border-slate-600">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={`${branding.company_name} Logo`} 
                className="h-8 w-8 object-contain mr-2"
              />
            ) : (
              <img 
                src="/TavPlans_Logo.png" 
                alt="TavPlans Logo" 
                className="h-8 w-8 object-contain mr-2"
              />
            )}
            <div>
              <h1 className="text-white font-bold text-base">{branding.company_name}</h1>
              <p className="text-slate-400 text-xs">Financial Modeling</p>
            </div>
          </div>
          
          {/* User Info with Logout - Compact */}
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-white text-xs font-medium">{user?.full_name?.split(' ')[0]}</p>
              <button 
                onClick={handleLogout}
                className="text-red-300 hover:text-red-200 text-xs transition-colors"
              >
                Logout
              </button>
            </div>
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-blue-600 text-white text-xs">
                {getUserInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>

        {/* Business Plan Info - Only show for business/hybrid users, hide during loading and for affiliateOnly */}
        {userTypeLoading && (
          <div className="space-y-2 mb-3">
            <div className="bg-slate-800/30 rounded-lg p-2 animate-pulse">
              <div className="h-3 bg-slate-700 rounded w-3/4 mb-1"></div>
              <div className="h-2 bg-slate-700 rounded w-1/2"></div>
            </div>
          </div>
        )}
        
        {!userTypeLoading && (userType === 'businessOnly' || userType === 'hybrid') && (
          <>
            {/* Language Switcher - Hidden for now, English is default */}
            {/* 
            <div className="mb-2 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <p className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Language</p>
              <LanguageSwitcher />
            </div>
            */}

            {/* Currency Selector - Always Visible */}
            {currentPlan && (
              <div className="mb-2 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                <p className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Display Currency</p>
                <CurrencySelector />
              </div>
            )}
            
            {/* Subscription Status with Upgrade - Always Visible */}
            <div className="mb-2">
              <SubscriptionStatus className="!p-2 !text-xs" />
            </div>

            {/* Business Plan Management - Collapsible */}
            <div className="space-y-1.5 mb-3">
              {currentPlan && (
                <>
                  <button
                    onClick={() => setBusinessPlanExpanded(!businessPlanExpanded)}
                    className="flex items-center justify-between w-full text-slate-400 hover:text-slate-300 text-xs font-medium uppercase tracking-wider transition-colors"
                  >
                    <span>Business Plan List</span>
                    <ChevronDown 
                      className={`h-3 w-3 transition-transform ${businessPlanExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {businessPlanExpanded && (
                    <div className="bg-slate-800/50 rounded-lg p-2">
                      <p className="text-white font-medium text-xs truncate">
                        {currentPlan.company_name}
                      </p>
                      <p className="text-slate-400 text-xs truncate">
                        {currentPlan.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-300 hover:text-white text-xs h-6 px-2 flex-1"
                          onClick={() => navigate('/business-plans')}
                        >
                          Switch
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-300 hover:text-blue-200 text-xs h-6 px-2 flex-1"
                          onClick={() => {
                            // Check if Starter tier at limit
                            const userTier = entitlements?.tier?.toLowerCase();
                            if (userTier === 'starter' && !canCreate('business_plans')) {
                              // Show CTA
                              toast.error(
                                <div className="space-y-2">
                                  <p className="font-bold">Business Plan Limit Reached</p>
                                  <p className="text-sm">Upgrade to Professional or buy an add-on to create more business plans.</p>
                                  <div className="flex gap-2 mt-2">
                                    <button 
                                      onClick={() => window.location.href = '/subscription'}
                                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                    >
                                      View Subscription
                                    </button>
                                    <button 
                                      onClick={() => window.location.href = '/add-ons'}
                                      className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                    >
                                      Browse Add-ons
                                    </button>
                                  </div>
                                </div>,
                                { duration: 10000 }
                              );
                            } else {
                              // Open create form
                              setShowCreateBusinessPlan(true);
                            }
                          }}
                        >
                          + New
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {!businessPlanExpanded && (
                    <div className="text-xs text-slate-400 px-2">
                      {currentPlan.company_name}
                    </div>
                  )}
                </>
              )}
              
              {!currentPlan && (
                <div className="bg-amber-800/20 border border-amber-600/30 rounded-lg p-2">
                  <p className="text-amber-300 text-xs font-medium">No Business Plan</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-amber-200 hover:text-white text-xs mt-1 h-6 px-2"
                    onClick={() => setShowCreateBusinessPlan(true)}
                  >
                    Create Plan
                  </Button>
                </div>
              )}
            </div>

            {/* Scenario Selector - Collapsible */}
            {scenarios.length > 0 && (
              <div className="space-y-1.5">
                <button
                  onClick={() => setScenariosExpanded(!scenariosExpanded)}
                  className="flex items-center justify-between w-full text-slate-400 hover:text-slate-300 text-xs font-medium uppercase tracking-wider transition-colors"
                >
                  <span>Active Scenario</span>
                  <ChevronDown 
                    className={`h-3 w-3 transition-transform ${scenariosExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
                
                {scenariosExpanded && (
                  <div className="flex flex-wrap gap-1">
                    {scenarios.map((scenario) => (
                      <button
                        key={scenario.id}
                        onClick={() => setActiveScenario(scenario.name)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          activeScenario === scenario.name
                            ? "bg-blue-600 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {scenario.name.charAt(0).toUpperCase() + scenario.name.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
                
                {!scenariosExpanded && activeScenario && (
                  <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium inline-block">
                    {activeScenario.charAt(0).toUpperCase() + activeScenario.slice(1)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto p-4">
        {navigationItems.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-4">
            <button
              onClick={() => toggleSection(sectionIndex)}
              className="flex items-center justify-between w-full text-slate-400 hover:text-slate-300 text-xs font-medium uppercase tracking-wider px-3 py-2 transition-colors"
            >
              <span>{section.section}</span>
              <ChevronDown 
                className={`h-3 w-3 transition-transform ${collapsedSections[sectionIndex] ? '' : 'rotate-180'}`}
              />
            </button>
            
            {!collapsedSections[sectionIndex] && (
              <div className="space-y-1 mt-1">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  
                  // Handle action items (like "Create Business Plan" for affiliate-only users)
                  if (item.action) {
                    return (
                      <button
                        key={item.action}
                        onClick={() => {
                          if (item.action === 'createBusinessPlan') {
                          setShowCreateBusinessPlan(true);
                        }
                      }}
                      className="nav-item w-full text-left"
                    >
                      <Icon className="h-4 w-4 mr-3" />
                      <span className="font-medium text-sm">{item.label}</span>
                    </button>
                  );
                }
                
                // Handle regular path items
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    <span className="font-medium text-sm">{item.label}</span>
                    {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </Link>
                );
              })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Business Plan Manager Dialog */}
      <Dialog open={showBusinessPlanManager} onOpenChange={setShowBusinessPlanManager}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch Business Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="max-h-60 overflow-y-auto space-y-2">
              {businessPlans.length === 0 ? (
                <p className="text-slate-500 text-sm">Loading business plans...</p>
              ) : (
                businessPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`p-3 border rounded-lg transition-colors ${
                      currentPlan?.id === plan.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div 
                      className="cursor-pointer"
                      onClick={() => {
                        setCurrentPlan(plan);
                        setShowBusinessPlanManager(false);
                        toast.success(`Switched to ${plan.name}`);
                      }}
                    >
                      <p className="font-medium">{plan.company_name}</p>
                      <p className="text-sm text-slate-600">{plan.name}</p>
                      <p className="text-xs text-slate-500">{plan.currency} • Fiscal: {getMonthName(plan.fiscal_year_start)}</p>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-2 pt-2 border-t border-slate-200">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 h-8 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPlan(plan);
                        }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-1 h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePlan(plan);
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="border-t pt-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowBusinessPlanManager(false);
                  setShowCreateBusinessPlan(true);
                }}
              >
                Create New Business Plan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Business Plan Dialog with Subsidiary Selection */}
      <CreateBusinessPlanDialog 
        open={showCreateBusinessPlan}
        onOpenChange={setShowCreateBusinessPlan}
        subsidiaries={subsidiaries}
        refreshUserType={refreshUserType}
        onPlanCreated={(newPlan) => {
          setCurrentPlan(newPlan);
          setShowCreateBusinessPlan(false);
          toast.success("Business plan created successfully!");
          // Refresh user type to update navigation
          if (refreshUserType) {
            refreshUserType();
          }
        }}
      />

      {/* Edit Business Plan Dialog */}
      <EditBusinessPlanDialog 
        open={showEditBusinessPlan}
        onOpenChange={setShowEditBusinessPlan}
        plan={editingPlan}
        subsidiaries={subsidiaries}
        onPlanUpdated={(updatedData) => {
          updatePlan(editingPlan.id, updatedData);
          setShowEditBusinessPlan(false);
          setEditingPlan(null);
        }}
      />
    </nav>
  );
};

// Create Business Plan Dialog Component
const CreateBusinessPlanDialog = ({ open, onOpenChange, subsidiaries, onPlanCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    currency: "USD",
    fiscal_year_start: 1,
    subsidiary_id: ""
  });
  const [loading, setLoading] = useState(false);
  const { createNewPlan } = useBusinessPlan();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
        company_name: "",
        currency: "USD",
        fiscal_year_start: 1,
        subsidiary_id: ""
      });
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company_name.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (subsidiaries.length > 0 && !formData.subsidiary_id) {
      toast.error("Please select a subsidiary for this business plan");
      return;
    }

    setLoading(true);
    try {
      const newPlan = await createNewPlan(formData);
      onPlanCreated(newPlan);
    } catch (error) {
      toast.error("Failed to create business plan");
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Business Plan</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Enterprise Subsidiary Selection */}
          {subsidiaries.length > 0 && (
            <div className="space-y-2">
              <Label>Subsidiary/Company *</Label>
              <Select value={formData.subsidiary_id} onValueChange={(value) => 
                setFormData(prev => ({...prev, subsidiary_id: value}))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="Select subsidiary for this business plan" />
                </SelectTrigger>
                <SelectContent>
                  {subsidiaries.map((subsidiary) => (
                    <SelectItem key={subsidiary.id} value={subsidiary.id}>
                      {subsidiary.name} ({subsidiary.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-600">
                This business plan will be created for the selected subsidiary
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business Plan Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., 2025 Growth Plan"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({...prev, company_name: e.target.value}))}
                placeholder="e.g., Tech Startup Inc."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => 
                setFormData(prev => ({...prev, currency: value}))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fiscal Year Start</Label>
              <Select value={formData.fiscal_year_start.toString()} onValueChange={(value) => 
                setFormData(prev => ({...prev, fiscal_year_start: parseInt(value)}))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Business Plan"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Business Plan Dialog Component
const EditBusinessPlanDialog = ({ open, onOpenChange, plan, subsidiaries, onPlanUpdated }) => {
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    currency: "USD",
    fiscal_year_start: 1,
    subsidiary_id: ""
  });
  const [loading, setLoading] = useState(false);

  // Load plan data when dialog opens
  useEffect(() => {
    if (open && plan) {
      setFormData({
        name: plan.name || "",
        company_name: plan.company_name || "",
        currency: plan.currency || "USD",
        fiscal_year_start: plan.fiscal_year_start || 1,
        subsidiary_id: plan.subsidiary_id || ""
      });
    }
  }, [open, plan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company_name.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      onPlanUpdated(formData);
    } catch (error) {
      toast.error("Failed to update business plan");
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Business Plan</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Enterprise Subsidiary Selection */}
          {subsidiaries.length > 0 && (
            <div className="space-y-2">
              <Label>Subsidiary/Company *</Label>
              <Select value={formData.subsidiary_id} onValueChange={(value) => 
                setFormData(prev => ({...prev, subsidiary_id: value}))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="Select subsidiary for this business plan" />
                </SelectTrigger>
                <SelectContent>
                  {subsidiaries.map((subsidiary) => (
                    <SelectItem key={subsidiary.id} value={subsidiary.id}>
                      {subsidiary.name} ({subsidiary.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business Plan Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., 2025 Growth Plan"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({...prev, company_name: e.target.value}))}
                placeholder="e.g., Tech Startup Inc."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => 
                setFormData(prev => ({...prev, currency: value}))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fiscal Year Start</Label>
              <Select value={formData.fiscal_year_start.toString()} onValueChange={(value) => 
                setFormData(prev => ({...prev, fiscal_year_start: parseInt(value)}))
              }>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Business Plan"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Helper function for month names
const getMonthName = (monthNumber) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return months[monthNumber - 1] || "Jan";
};

export default Navigation;